import React, { useState, useEffect } from 'react';
import { getPayrollEmployees, savePayrollEmployee, deletePayrollEmployee } from '../../../services/payrollService';

const PAY_TYPES = [
  { value: 'ADP', label: 'ADP Only (Hourly)' },
  { value: 'Salaried', label: 'Salaried (Fixed Weekly ADP)' },
  { value: 'Cash', label: 'Cash Only' },
  { value: 'Combo-HourlyRate', label: 'Combo — Hourly Split (ADP rate + cash top-up)' },
  { value: 'Combo-Percentage', label: 'Combo — Percentage Split' },
  { value: 'Combo-JobType', label: 'Combo — By Job Type' },
  { value: 'Apprentice', label: 'Apprentice (Fixed Stipend)' },
];

const DEPARTMENTS = ['AR', 'CC', 'Both'];

function emptyEmployee() {
  return {
    id: '', firstName: '', lastName: '', connectTeamName: '', active: true,
    department: 'AR', payType: 'ADP', adpRate: '', cashRate: '',
    comboJobTypeADP: '', comboHourlyAdpRate: '', comboCashTopUp: '',
    comboPercentageADP: '', apprenticeWeeklyADP: '', apprenticeWeeklyCash: '',
    apprenticeHourlyRate: '', notes: '',
  };
}

export default function PayrollEmployees() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { loadEmployees(); }, []);

  async function loadEmployees() {
    setLoading(true);
    const res = await getPayrollEmployees();
    if (res.success) setEmployees(res.data.sort((a, b) => (a.lastName || '').localeCompare(b.lastName || '')));
    setLoading(false);
  }

  async function handleSave() {
    if (!editing.firstName || !editing.lastName) return setError('First and last name are required.');
    setError('');
    setSaving(true);
    const res = await savePayrollEmployee(editing);
    setSaving(false);
    if (res.success) { setEditing(null); loadEmployees(); }
    else setError('Error: ' + res.error);
  }

  async function handleDelete(emp) {
    if (!window.confirm(`Delete ${emp.firstName} ${emp.lastName}?`)) return;
    const res = await deletePayrollEmployee(emp.id);
    if (res.success) loadEmployees();
  }

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    setEditing(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  }

  return (
    <div>
      <div className="pr-header">
        <h2>Employee Settings</h2>
        <button className="pr-btn-primary" onClick={() => setEditing(emptyEmployee())}>+ Add Employee</button>
      </div>

      {error && <div className="pr-error">{error}</div>}

      {editing && (
        <div className="pr-form-overlay">
          <div className="pr-form-panel">
            <div className="pr-form-header">
              <h3>{editing.id ? 'Edit Employee' : 'New Employee'}</h3>
              <button className="pr-btn-secondary" onClick={() => setEditing(null)}>Cancel</button>
            </div>

            <div className="pr-form-row">
              <div className="pr-form-group">
                <label>First Name</label>
                <input type="text" name="firstName" value={editing.firstName} onChange={handleChange} />
              </div>
              <div className="pr-form-group">
                <label>Last Name</label>
                <input type="text" name="lastName" value={editing.lastName} onChange={handleChange} />
              </div>
            </div>
            <div className="pr-form-group">
              <label>ConnectTeam Name (if different)</label>
              <input type="text" name="connectTeamName" value={editing.connectTeamName} onChange={handleChange} placeholder="Leave blank if same as first + last" />
            </div>
            <div className="pr-form-row">
              <div className="pr-form-group">
                <label>Department</label>
                <select name="department" value={editing.department} onChange={handleChange}>
                  {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div className="pr-form-group">
                <label>Pay Type</label>
                <select name="payType" value={editing.payType} onChange={handleChange}>
                  {PAY_TYPES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
              <div className="pr-form-group" style={{flex:'0 0 80px'}}>
                <label>Active</label>
                <input type="checkbox" name="active" checked={editing.active === true || editing.active === 'TRUE'} onChange={handleChange} style={{marginTop:'10px'}} />
              </div>
            </div>

            {editing.department === 'Both' && (
              <div className="pr-form-section">
                <div className="pr-form-section-title">AR Rate Settings</div>
                <div className="pr-form-row">
                  <div className="pr-form-group">
                    <label>AR Pay Type</label>
                    <select name="arPayType" value={editing.arPayType || 'Cash'} onChange={handleChange}>
                      <option value="ADP">ADP</option>
                      <option value="Cash">Cash</option>
                    </select>
                  </div>
                  <div className="pr-form-group">
                    <label>AR Rate ($/hr)</label>
                    <input type="number" step="0.01" name="adpRate" value={editing.adpRate} onChange={handleChange} placeholder="0.00" />
                  </div>
                </div>
                <div className="pr-form-section-title" style={{marginTop:'12px'}}>CC Rate Settings</div>
                <div className="pr-form-row">
                  <div className="pr-form-group">
                    <label>CC Pay Type</label>
                    <select name="ccPayType" value={editing.ccPayType || 'Cash'} onChange={handleChange}>
                      <option value="ADP">ADP</option>
                      <option value="Cash">Cash</option>
                    </select>
                  </div>
                  <div className="pr-form-group">
                    <label>CC Rate ($/hr)</label>
                    <input type="number" step="0.01" name="cashRate" value={editing.cashRate} onChange={handleChange} placeholder="0.00" />
                  </div>
                </div>
              </div>
            )}
            {editing.department !== 'Both' && ['ADP','Cash','Combo-HourlyRate','Combo-Percentage','Combo-JobType'].includes(editing.payType) && (
              <div className="pr-form-row">
                <div className="pr-form-group">
                  <label>ADP Hourly Rate</label>
                  <input type="number" step="0.01" name="adpRate" value={editing.adpRate} onChange={handleChange} placeholder="0.00" />
                </div>
                {editing.payType === 'Cash' && (
                  <div className="pr-form-group">
                    <label>Cash Hourly Rate</label>
                    <input type="number" step="0.01" name="cashRate" value={editing.cashRate} onChange={handleChange} placeholder="Same as ADP if blank" />
                  </div>
                )}
              </div>
            )}

            {editing.payType === 'Salaried' && (
              <div className="pr-form-section">
                <div className="pr-form-section-title">Salary Settings</div>
                <div className="pr-form-row">
                  <div className="pr-form-group">
                    <label>Weekly Salary Amount ($)</label>
                    <input type="number" step="0.01" name="apprenticeWeeklyADP" value={editing.apprenticeWeeklyADP} onChange={handleChange} placeholder="e.g. 1200.00" />
                    <small>Goes through ADP each pay period</small>
                  </div>
                  <div className="pr-form-group">
                    <label>Cash Top-Up Rate ($/hr)</label>
                    <input type="number" step="0.01" name="comboCashTopUp" value={editing.comboCashTopUp} onChange={handleChange} placeholder="e.g. 15.00" />
                    <small>Hourly rate for extra cash hours (markets, OT)</small>
                  </div>
                  <div className="pr-form-group">
                    <label>Fixed Weekly Cash ($)</label>
                    <input type="number" step="0.01" name="apprenticeWeeklyCash" value={editing.apprenticeWeeklyCash} onChange={handleChange} placeholder="e.g. 100.00" />
                    <small>Fixed cash amount paid every pay period</small>
                  </div>
                </div>
              </div>
            )}

            {editing.payType === 'Combo-HourlyRate' && (
              <div className="pr-form-section">
                <div className="pr-form-section-title">Hourly Split</div>
                <div className="pr-form-row">
                  <div className="pr-form-group">
                    <label>ADP Rate/hr</label>
                    <input type="number" step="0.01" name="comboHourlyAdpRate" value={editing.comboHourlyAdpRate} onChange={handleChange} placeholder="17.00" />
                  </div>
                  <div className="pr-form-group">
                    <label>Cash Top-Up/hr</label>
                    <input type="number" step="0.01" name="comboCashTopUp" value={editing.comboCashTopUp} onChange={handleChange} placeholder="1.50" />
                  </div>
                </div>
              </div>
            )}

            {editing.payType === 'Combo-Percentage' && (
              <div className="pr-form-section">
                <div className="pr-form-section-title">Percentage Split</div>
                <div className="pr-form-group">
                  <label>ADP Percentage (%)</label>
                  <input type="number" step="1" name="comboPercentageADP" value={editing.comboPercentageADP} onChange={handleChange} placeholder="60" />
                  <small>Remaining % paid cash</small>
                </div>
              </div>
            )}

            {editing.payType === 'Combo-JobType' && (
              <div className="pr-form-section">
                <div className="pr-form-section-title">Job Type → ADP Mapping</div>
                <div className="pr-form-group">
                  <label>ADP Job Types (comma separated)</label>
                  <input type="text" name="comboJobTypeADP" value={editing.comboJobTypeADP} onChange={handleChange} placeholder="e.g. Day Bake, Night Bake, Ferments" />
                  <small>Any job type NOT listed here will be paid cash</small>
                </div>
              </div>
            )}

            {editing.payType === 'Apprentice' && (
              <div className="pr-form-section">
                <div className="pr-form-section-title">Apprentice Stipend</div>
                <div className="pr-form-row">
                  <div className="pr-form-group">
                    <label>Weekly ADP ($)</label>
                    <input type="number" step="0.01" name="apprenticeWeeklyADP" value={editing.apprenticeWeeklyADP} onChange={handleChange} placeholder="400.00" />
                  </div>
                  <div className="pr-form-group">
                    <label>Weekly Cash ($)</label>
                    <input type="number" step="0.01" name="apprenticeWeeklyCash" value={editing.apprenticeWeeklyCash} onChange={handleChange} placeholder="200.00" />
                  </div>
                  <div className="pr-form-group">
                    <label>Extra Hrs Rate</label>
                    <input type="number" step="0.01" name="apprenticeHourlyRate" value={editing.apprenticeHourlyRate} onChange={handleChange} placeholder="15.00" />
                  </div>
                </div>
              </div>
            )}

            <div className="pr-form-group">
              <label>Notes</label>
              <input type="text" name="notes" value={editing.notes} onChange={handleChange} placeholder="Any notes..." />
            </div>

            <div className="pr-form-actions">
              <button className="pr-btn-secondary" onClick={() => setEditing(null)}>Cancel</button>
              <button className="pr-btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save Employee'}
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="pr-loading">Loading...</div>
      ) : employees.length === 0 ? (
        <div className="pr-empty">No employees yet. Add your first employee above.</div>
      ) : (
        <table className="pr-table">
          <thead>
            <tr><th>Name</th><th>Dept</th><th>Pay Type</th><th>Rate</th><th>Status</th><th></th></tr>
          </thead>
          <tbody>
            {employees.map(emp => (
              <tr key={emp.id}>
                <td className="pr-td-name">{emp.firstName} {emp.lastName}</td>
                <td><span className={`pr-dept-badge pr-dept-${emp.department?.toLowerCase()}`}>{emp.department}</span></td>
                <td style={{fontSize:'0.82rem'}}>{PAY_TYPES.find(p => p.value === emp.payType)?.label || emp.payType}</td>
                <td>{emp.adpRate ? `$${emp.adpRate}/hr` : '—'}</td>
                <td>{emp.active === false || emp.active === 'FALSE' ? <span className="pr-inactive-badge">Inactive</span> : <span className="pr-active-badge">Active</span>}</td>
                <td>
                  <button className="pr-btn-edit" onClick={() => setEditing({...emp})}>Edit</button>
                  <button className="pr-btn-delete" onClick={() => handleDelete(emp)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
